import type { NoticeLevel } from '@shared/designSystem/NoticeBanner'
import type { ContentLanguage } from '@shared/localization/languageForCountryCode'

/**
 * Notice chrome follows the organization's language (the same one the notice
 * text is resolved in), not the app-wide `.localized()` language, so a Brazilian
 * organization never sees "Ver más" next to a Portuguese notice.
 */
export interface PlatformNoticesStrings {
    levels: Record<NoticeLevel, string>
    dismiss: (title: string) => string
    more: string
    less: string
    moreCount: (count: number) => string
    acknowledge: string
    scopeOrganization: string
    scopeOrganizationNamed: (name: string) => string
    scopeGeneral: string
    updated: string
    panelTitle: (count: number) => string
    panelClose: string
    newDeferred: string
}

const plural = (count: number, singular: string, pluralForm: string) => (count === 1 ? singular : pluralForm)

export const PLATFORM_NOTICES_STRINGS: Record<ContentLanguage, PlatformNoticesStrings> = {
    es: {
        levels: { critical: 'Aviso crítico', warning: 'Advertencia', communication: 'Comunicación' },
        dismiss: (title) => `Descartar aviso: ${title}`,
        more: 'Ver más',
        less: 'Ver menos',
        moreCount: (count) => `+${count} ${plural(count, 'aviso', 'avisos')}`,
        acknowledge: 'Entendido',
        scopeOrganization: 'Para tu organización',
        scopeOrganizationNamed: (name) => `Para ${name}`,
        scopeGeneral: 'Aviso general',
        updated: 'Actualizado',
        panelTitle: (count) => `Avisos de la plataforma (${count})`,
        panelClose: 'Cerrar avisos',
        newDeferred: '1 aviso nuevo',
    },
    pt: {
        levels: { critical: 'Aviso crítico', warning: 'Advertência', communication: 'Comunicação' },
        dismiss: (title) => `Dispensar aviso: ${title}`,
        more: 'Ver mais',
        less: 'Ver menos',
        moreCount: (count) => `+${count} ${plural(count, 'aviso', 'avisos')}`,
        acknowledge: 'Entendi',
        scopeOrganization: 'Para a sua organização',
        scopeOrganizationNamed: (name) => `Para ${name}`,
        scopeGeneral: 'Aviso geral',
        updated: 'Atualizado',
        panelTitle: (count) => `Avisos da plataforma (${count})`,
        panelClose: 'Fechar avisos',
        newDeferred: '1 aviso novo',
    },
    en: {
        levels: { critical: 'Critical notice', warning: 'Warning', communication: 'Announcement' },
        dismiss: (title) => `Dismiss notice: ${title}`,
        more: 'Show more',
        less: 'Show less',
        moreCount: (count) => `+${count} ${plural(count, 'notice', 'notices')}`,
        acknowledge: 'Got it',
        scopeOrganization: 'For your organization',
        scopeOrganizationNamed: (name) => `For ${name}`,
        scopeGeneral: 'General notice',
        updated: 'Updated',
        panelTitle: (count) => `Platform notices (${count})`,
        panelClose: 'Close notices',
        newDeferred: '1 new notice',
    },
}
